import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import './GeneralSettings.css';

const GeneralSettings = () => {
  const { user, updateUser } = useAuth();
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [subEmails, setSubEmails] = useState([]);
  const [newSubEmail, setNewSubEmail] = useState('');
  const [isAddingSubEmail, setIsAddingSubEmail] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    // Show verification section if email is not verified, or keep it visible if recently verified
    if (user) {
      setShowEmailVerification(true);
    }
    fetchSubEmails();
    
    // Check for verification token in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setVerificationToken(token);
      // Auto-verify if token is present
      handleVerifyEmailWithToken(token);
    }
  }, [user]);

  // Timer countdown effect
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prevTimer) => {
          if (prevTimer <= 1) {
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [resendTimer]);

  const fetchSubEmails = async () => {
    try {
      const response = await ApiService.get('/user/associated-emails');
      if (response.success) {
        setSubEmails(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching associated emails:', error);
    }
  };

  const handleSendEmailVerification = async () => {
    // Prevent sending if timer is active
    if (resendTimer > 0) {
      return;
    }

    setIsSendingVerification(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const response = await ApiService.post('/profile/send-email-verification');
      if (response.success) {
        setSuccessMessage('Verification email sent! Please check your inbox.');
        // Start 60 second timer
        setResendTimer(60);
      } else {
        setErrors({ general: response.message || 'Failed to send verification email. Please try again.' });
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setErrors({ general: 'Failed to send verification email. Please try again.' });
    } finally {
      setIsSendingVerification(false);
    }
  };

  const handleVerifyEmailWithToken = async (token) => {
    if (!token) return;

    setIsVerifying(true);
    setErrors({});
    setSuccessMessage('');

    try {
      // First, try to verify primary email
      try {
        const primaryResponse = await ApiService.post('/profile/verify-email', { token });
        if (primaryResponse.success) {
          // Primary email verified successfully
          setSuccessMessage(primaryResponse.message || 'Email verified successfully!');
          
          // Update user state in AuthContext
          if (primaryResponse.data?.user) {
            updateUser({
              is_email_verified: primaryResponse.data.user.is_email_verified,
              email_verified_at: primaryResponse.data.user.email_verified_at,
              profile_completed: primaryResponse.data.user.profile_completed,
            });
          }
          
          // Refresh sub-emails list
          fetchSubEmails();
          // Clear URL params
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
      } catch (primaryError) {
        // If primary email verification fails, try associated email verification
        console.log('Primary email verification failed, trying associated email...', primaryError);
      }

      // Try to verify as associated email
      const associatedResponse = await ApiService.post('/user/associated-emails/verify', { token });
      if (associatedResponse.success) {
        setSuccessMessage('Associated email verified successfully!');
        fetchSubEmails();
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        setErrors({ general: associatedResponse.message || 'Invalid verification token. Please check the token and try again.' });
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setErrors({ general: error.data?.message || error.message || 'Invalid verification token. Please check the token and try again.' });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!verificationToken.trim()) {
      setErrors({ token: 'Please enter the verification token' });
      return;
    }

    setIsVerifying(true);
    setErrors({});
    setSuccessMessage('');

    try {
      // First, try to verify primary email
      try {
        const primaryResponse = await ApiService.post('/profile/verify-email', { token: verificationToken });
        if (primaryResponse.success) {
          // Primary email verified successfully
          setSuccessMessage(primaryResponse.message || 'Email verified successfully!');
          
          // Update user state in AuthContext
          if (primaryResponse.data?.user) {
            updateUser({
              is_email_verified: primaryResponse.data.user.is_email_verified,
              email_verified_at: primaryResponse.data.user.email_verified_at,
              profile_completed: primaryResponse.data.user.profile_completed,
            });
          }
          
          // Refresh sub-emails list
          fetchSubEmails();
          setVerificationToken('');
          return;
        }
      } catch (primaryError) {
        // If primary email verification fails, try associated email verification
        console.log('Primary email verification failed, trying associated email...', primaryError);
      }

      // Try to verify as associated email
      const associatedResponse = await ApiService.post('/user/associated-emails/verify', { token: verificationToken });
      if (associatedResponse.success) {
        setSuccessMessage('Associated email verified successfully!');
        fetchSubEmails();
        setVerificationToken('');
      } else {
        setErrors({ general: associatedResponse.message || 'Invalid verification token. Please check the token and try again.' });
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setErrors({ general: error.data?.message || error.message || 'Invalid verification token. Please check the token and try again.' });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleAddSubEmail = async () => {
    if (!newSubEmail.trim()) {
      setErrors({ subEmail: 'Please enter an email address' });
      return;
    }

    if (!isValidEmail(newSubEmail)) {
      setErrors({ subEmail: 'Please enter a valid email address' });
      return;
    }

    setIsAddingSubEmail(true);
    setErrors({});

    try {
      const response = await ApiService.post('/user/associated-emails', { email: newSubEmail });
      if (response.success) {
        setSuccessMessage('Associated email added successfully! Verification email sent.');
        setNewSubEmail('');
        fetchSubEmails();
      } else {
        setErrors({ subEmail: response.message || 'Failed to add associated email. Please try again.' });
      }
    } catch (error) {
      console.error('Add associated email error:', error);
      setErrors({ subEmail: 'Failed to add associated email. Please try again.' });
    } finally {
      setIsAddingSubEmail(false);
    }
  };

  const handleRemoveSubEmail = async (emailId) => {
    try {
      const response = await ApiService.delete(`/user/associated-emails/${emailId}`);
      if (response.success) {
        setSuccessMessage('Associated email removed successfully!');
        fetchSubEmails();
      } else {
        setErrors({ general: response.message || 'Failed to remove associated email. Please try again.' });
      }
    } catch (error) {
      console.error('Remove associated email error:', error);
      setErrors({ general: 'Failed to remove associated email. Please try again.' });
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <div className="general-settings-container">
      <div className="general-settings-header">
        <h2>Email Settings</h2>
        <p>Manage your email verification and sub-email addresses</p>
      </div>

      <div className="general-settings-content">
        {/* Email Verification Section */}
        {showEmailVerification && (
          <div className={`settings-section email-verification-section ${user?.is_email_verified ? 'verified' : ''}`}>
            <div className="section-header">
              <h3>📧 Email Verification</h3>
              <p>Verify your primary email address to secure your account</p>
            </div>

            <div className="email-verification-content">
              <div className="verification-info">
                <div className="verification-status">
                  <div className={`status-icon ${user?.is_email_verified ? 'verified' : 'unverified'}`}>
                    {user?.is_email_verified ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22,4 12,14.01 9,11.01"></polyline>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                    )}
                  </div>
                  <div className="status-text">
                    <h4>{user?.is_email_verified ? 'Email Verified' : 'Email Not Verified'}</h4>
                    <p>
                      {user?.is_email_verified ? (
                        <>Your email address <strong>{user?.email}</strong> has been verified successfully!</>
                      ) : (
                        <>We've sent a verification link to <strong>{user?.email}</strong>. Please check your inbox and click the link to verify your email address.</>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {!user?.is_email_verified && (
                <>
                  <div className="verification-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={handleSendEmailVerification}
                      disabled={isSendingVerification || resendTimer > 0}
                    >
                      {isSendingVerification ? (
                        <>
                          <span className="spinner"></span>
                          Sending...
                        </>
                      ) : resendTimer > 0 ? (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12,6 12,12 16,14"></polyline>
                          </svg>
                          Wait {resendTimer}s
                        </>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                          </svg>
                          Resend Verification Email
                        </>
                      )}
                    </button>
                    {resendTimer > 0 && (
                      <div className="timer-info">
                        <p>Please wait {resendTimer} second{resendTimer !== 1 ? 's' : ''} before requesting another email.</p>
                      </div>
                    )}
                  </div>

                  <div className="manual-verification">
                    <h4>Manual Verification</h4>
                    <p>If you received a verification token for your primary email or an associated email, enter it below:</p>
                    <div className="token-input-group">
                      <input
                        type="text"
                        value={verificationToken}
                        onChange={(e) => setVerificationToken(e.target.value)}
                        placeholder="Enter verification token"
                        className={errors.token ? 'error' : ''}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={handleVerifyEmail}
                        disabled={isVerifying || !verificationToken.trim()}
                      >
                        {isVerifying ? (
                          <>
                            <span className="spinner"></span>
                            Verifying...
                          </>
                        ) : (
                          'Verify Email'
                        )}
                      </button>
                    </div>
                    {errors.token && <span className="field-error">{errors.token}</span>}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Associated Emails Management Section */}
        <div className="settings-section associated-emails-section">
          <div className="section-header">
            <h3>📧 Associated Emails</h3>
            <p>Manage all email addresses associated with your account for notifications and login</p>
          </div>

          <div className="associated-emails-content">
            <div className="add-associated-email">
              <div className="add-email-form">
                <input
                  type="email"
                  value={newSubEmail}
                  onChange={(e) => setNewSubEmail(e.target.value)}
                  placeholder="Enter email address"
                  className={errors.subEmail ? 'error' : ''}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleAddSubEmail}
                  disabled={isAddingSubEmail || !newSubEmail.trim()}
                >
                  {isAddingSubEmail ? (
                    <>
                      <span className="spinner"></span>
                      Adding...
                    </>
                  ) : (
                    'Add Email'
                  )}
                </button>
              </div>
              {errors.subEmail && <span className="field-error">{errors.subEmail}</span>}
            </div>

            <div className="associated-emails-list">
              {subEmails.length > 0 || user?.email ? (
                <div className="emails-grid">
                  {/* Main Email */}
                  {user?.email && (
                    <div className="email-item main-email">
                      <div className="email-info">
                        <div className="email-address">{user.email}</div>
                        <div className="email-status">
                          <span className={`status-badge ${user.is_email_verified ? 'verified' : 'unverified'}`}>
                            {user.is_email_verified ? 'Verified' : 'Unverified'}
                          </span>
                          <span className="email-type">Primary</span>
                        </div>
                      </div>
                      <div className="email-actions">
                        <span className="primary-badge">Main Account</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Associated Emails */}
                  {subEmails.map((email) => (
                    <div key={email.id} className="email-item">
                      <div className="email-info">
                        <div className="email-address">{email.email}</div>
                        <div className="email-status">
                          <span className={`status-badge ${email.is_verified ? 'verified' : 'unverified'}`}>
                            {email.is_verified ? 'Verified' : 'Unverified'}
                          </span>
                        </div>
                      </div>
                      <button
                        className="remove-email-btn"
                        onClick={() => handleRemoveSubEmail(email.id)}
                        title="Remove email"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-associated-emails">
                  <div className="no-emails-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </div>
                  <h4>No Associated Emails</h4>
                  <p>Add additional email addresses to receive notifications and use for login</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="success-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22,4 12,14.01 9,11.01"></polyline>
            </svg>
            {successMessage}
          </div>
        )}

        {errors.general && (
          <div className="error-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            {errors.general}
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralSettings;
